import React, { useEffect, useState } from 'react';
import {
    Dialog,
    Grid2,
    Box,
    Typography,
    IconButton,
    Divider
} from '@mui/material';
import ComparisonSideBar from './ComparisonSideBar';
import CustomIcon from '../../../../../Components/CustomIcon';
import ComparisonChartSection from './ComparisonChartSection';
import { useTranslation } from 'react-i18next';
import { getNodesByIds } from '../../../../../Helper/DigitalTwin/DigitalTwinFunctions';
import { IDDisplayBox } from '../../../../../Theme/DigitalTwinStyles';
import CloseButton from '../../../../../Components/CloseButton';
import FullScreenDialog from '../../../../../Components/FullScreenDialog';
import { getCurrentTimestamp } from "../../../../../Helper/DatePicker/DateConverter";
import { usePagesContent, useBaseEntityIdLabel } from '../../../../../Hooks/usePagesContent';
import { buildComparisonCSV } from '../../../../../Helper/DigitalTwin/comparisonExportHelper';
import Cookies from "universal-cookie";

const ComparisonView = ({
    batteryID,
    batteryStructure = [],
    batteryInfo = {},
    batteryConfig = {},
    showBusDeviceInfo = () => {},
    vehicleInfo = {},
    dataGapDates = []
}) => {
    const [open, setOpen] = useState(false);
    const [selectedIds, setSelectedIds] = useState([])
    const [selectedNodes, setSelectedNodes] = useState([])
    const [selectedTab, setSelectedTab] = useState('')
    const [exportContext, setExportContext] = useState({
        metricsSeries: {},
        selectedMetrics: [],
        selectedNodes: [],
        primaryGranularity: 'raw',
        secondaryGranularity: 'avg',
        isLoaded: false,
    })
    const { t } = useTranslation(['common', 'digital_twin'])
    const pageContent = usePagesContent();
    const entityIdLabel = useBaseEntityIdLabel(t('common:device_id'));
    const MAX_METRICS = pageContent?.metaInfo?.globalConfig?.["multipartCompare.maxSignals"] || 4;
    const cookies = new Cookies();
    const orgName = cookies.get("orgName");
    const shouldShowDownloadButton = orgName?.toLowerCase() === "jbm";

    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    useEffect(() => {
        if (open) {
            setSelectedIds([])
            setSelectedTab('')
            setSelectedNodes([])
        }

    }, [open])

    useEffect(() => {
        let nodes = getNodesByIds(batteryStructure, selectedIds)
        setSelectedNodes(nodes)
    }, [selectedIds])

    const downloadCSVContent = (filename, content) => {
        const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
        if (navigator.msSaveBlob) {
            navigator.msSaveBlob(blob, filename);
        } else {
            const link = document.createElement("a");
            if (link.download !== undefined) {
                const url = URL.createObjectURL(blob);
                link.setAttribute("href", url);
                link.setAttribute("download", filename);
                link.style.visibility = "hidden";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        }
    };

    const handleDownloadSelectedData = () => {
        const csvContent = buildComparisonCSV({
            metricsSeries: exportContext.metricsSeries,
            selectedMetrics: exportContext.selectedMetrics,
            selectedNodes: exportContext.selectedNodes,
            batteryStructure,
            batteryInfo,
            vehicleInfo,
            maxMetrics: MAX_METRICS,
        });
        if (!csvContent) {
            return;
        }
        const timestamp = getCurrentTimestamp("YYYY_MM_DD_HH_mm_ss");
        const csvName = `${timestamp}_BatteryVitals_Compare`;
        downloadCSVContent(csvName, csvContent);
    };

    return (
        <Box>
            <Grid2 container gap={2} flexDirection={'column'}>
                <Typography variant="fw600s12c8D8D8D">{t('digital_twin:analyse')}</Typography>
                <Grid2 container alignItems={'center'} gap={1.5} onClick={handleOpen} sx={{
                    cursor: 'pointer',
                    '&:hover': {
                        '& .MuiTypography-root': {
                            color: '#4D44D8 !important'
                        }
                    }
                }}>
                    <CustomIcon name="battery-bar-icon" />
                    <Typography variant="fw500s12c000">{t('digital_twin:comparison_button_title')}</Typography>
                </Grid2>
            </Grid2>
            <FullScreenDialog
                open={open}
                handleClose={handleClose}
                containerStyles={{ overflowY: 'hidden', overflowX: 'hidden' }}>
                <Grid2 container justifyContent={'space-between'} alignItems={'center'} sx={{ py: 2, px: 2.5 }}>
                    <Grid2 container flexDirection={'column'} gap={0.5} >
                        <Typography variant="fw500s16lh20c000000">{t('digital_twin:comparison_dialog_title')}</Typography>
                        <Typography variant='fw400s12c686868' sx={{ lineHeight: '16px' }}>{t('digital_twin:comparison_dialog_subtitle')} </Typography>
                    </Grid2>
                    <Grid2 container justifyContent={'center'} alignItems={'center'} gap={1.8}>
                        {showBusDeviceInfo() === true ? (
                            <IDDisplayBox sx={{ gap: 0 }}>
                                <Typography variant="fw400s12c494949">{t('digital_twin:section_ids.bus_number')}:&nbsp;</Typography>
                                <Typography variant="fw400s12c494949">{vehicleInfo?.licensePlateNo}</Typography>
                            </IDDisplayBox>
                        ) : (
                            <IDDisplayBox sx={{ gap: 0 }}>
                                <Typography variant="fw400s12c494949">{entityIdLabel}:&nbsp;</Typography>
                                <Typography variant="fw400s12c494949">{batteryInfo?.deviceID}</Typography>
                            </IDDisplayBox>
                        )}

                        <CloseButton onClick={handleClose} />
                    </Grid2>
                </Grid2>

                <Divider />

                <Grid2 container sx={{ flexWrap: 'nowrap', height: '100%' }}>
                    <Grid2 sx={{ minWidth: '252px', flexShrink: 0, borderRight: '1px solid #E6E6E6' }}>
                        <ComparisonSideBar
                            batteryID={batteryID}
                            title="Battery System"
                            batteryStructure={batteryStructure}
                            batteryConfig={batteryConfig}
                            onChangeNode={(selectedIds) => { setSelectedIds(selectedIds) }}
                            onChangeTab={(tab) => { setSelectedTab(tab) }}
                        />
                    </Grid2>

                    <Grid2 sx={{ flexShrink: 0, flexGrow: 1, maxWidth: 'calc(100vw - 350px)' }}>
                        <ComparisonChartSection
                            batteryInfo={batteryInfo}
                            batteryID={batteryID}
                            selectedTab={selectedTab}
                            selectedIds={selectedIds}
                            selectedNodes={selectedNodes}
                            dataGapDates={dataGapDates}
                            showBusDeviceInfo={showBusDeviceInfo}
                            onExportDataChange={setExportContext}
                            onDownloadCSV={handleDownloadSelectedData}
                            downloadDisabled={false}
                            showDownloadButton={shouldShowDownloadButton && exportContext.isLoaded}
                        />
                    </Grid2>
                </Grid2>

            </FullScreenDialog>
        </Box>
    );
};

export default ComparisonView;
